import { ColDef, PostSortRowsParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CustomCellClickAction,
	GridCellConfig,
	postSortHandler,
	RemoveRowHandler,
	useRowSelectionHandler,
} from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import {
	defaultSYS2020_R01Dto,
	LANG_COLUMN_HEADERS,
	LANG_FIELDS,
	SYS2020_R01Dto,
} from './Dto_SYS2020_R01';
import { useSYS2020_R01CellChange } from './hooks/SYS2020_R01CellChange';
import useSYS2020_R01Store from './Store_SYS2020_R01';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

const COLUMN_STATE_KEY = 'SYS2020_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<SYS2020_R01Dto> | null>;
	defaultSYS2020_R01Row: () => SYS2020_R01Dto;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const Table_SYS2020_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultSYS2020_R01Row, handleInsertClick } = props;

	const {
		SYS2020_R01List, setSYS2020_R01List,
		removeSYS2020_R01List, updateSYS2020_R01List,
		deletedSYS2020_R01List, addDeletedSYS2020_R01List,
		selectedSYS2020_R01List, addSelectedSYS2020_R01List, removeSelectedSYS2020_R01List,
		initSelectedSYS2020_R01List, allSelectedSYS2020_R01List,
		originalSYS2020_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSYS2020_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS2020_R01List,
		defaultGridData: defaultSYS2020_R01Row(),
		selectedRow: selectedSYS2020_R01List,
		originalGridData: originalSYS2020_R01List,
		updateRow: (index: number, data: any) => updateSYS2020_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: ['commentCd'],
		pointerCells: ['description', ...LANG_FIELDS],
		insertedPointerCells: ['commentCd', 'description', ...LANG_FIELDS],
		commonPointerCells: [],
		alignLeftCells: ['commentCd', 'description', ...LANG_FIELDS],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SYS2020_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedSYS2020_R01List,
			deletedList: deletedSYS2020_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns, alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [selectedSYS2020_R01List, deletedSYS2020_R01List, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns, createCellClassRules]);

	const defaultEditableColumns = ['commentCd', 'description', ...LANG_FIELDS];

	const { handleCellChange } = useSYS2020_R01CellChange({
		updateSYS2020_R01List,
		originalSYS2020_R01List,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: SYS2020_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const langColumnDefs: ColDef<SYS2020_R01Dto>[] = useMemo(() =>
		LANG_FIELDS.map((field) => ({
			headerName: LANG_COLUMN_HEADERS[field], colId: field, field, width: 100,
			editable: true,
			pasteable: true,
	})), []);

	const baseColumnDefs: ColDef<SYS2020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SYS2020_R01Dto) => renderIndexCell(
				params,
				(innerParams) => getRowStyleFlags(innerParams, {
					deletedList: deletedSYS2020_R01List,
					selectedList: selectedSYS2020_R01List,
					dataList: SYS2020_R01List,
				}),
				(rowIndex, e) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) handleInsertClick(e, rowIndex);
					} else {
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData) => RemoveRowHandler(
					gridRef,
					SYS2020_R01List,
					rowData,
					setClickable,
					removeSYS2020_R01List,
					(valueIndex) => addDeletedSYS2020_R01List([valueIndex.toString()]),
				),
			),
		),
		{
			headerName: '코드', colId: 'commentCd', field: 'commentCd', width: 120,
			editable: (params) => params.data?.inserted === true,
			pasteable: true,
		},
		{
			headerName: '용도', colId: 'description', field: 'description', width: 140,
			editable: true,
			pasteable: true,
		},
		...langColumnDefs,
	], [deletedSYS2020_R01List, selectedSYS2020_R01List, SYS2020_R01List, renderIndexCell, handleInsertClick, gridRef, setClickable, removeSYS2020_R01List, addDeletedSYS2020_R01List, langColumnDefs, createCellClassRules]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SYS2020_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SYS2020_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: LANG_COLUMN_HEADERS,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);

	const cellClickActions: CustomCellClickAction<SYS2020_R01Dto>[] = useMemo(() => [{
		validate: (field) => defaultEditableColumns.includes(field),
		action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
	}], [defaultEditableColumns]);

	const handleCellClick = CreateCellClickHandler(SYS2020_R01List, 'valueIndex', cellClickActions);

	const handleRowClick = useRowSelectionHandler({
		dataList: SYS2020_R01List,
		idKey: 'valueIndex',
		selectedList: selectedSYS2020_R01List,
		deletedList: deletedSYS2020_R01List,
		addSelected: addSelectedSYS2020_R01List,
		removeSelected: removeSelectedSYS2020_R01List,
		clickable, setClickable,
	});

	const handlePostSort = useCallback((params: PostSortRowsParams<SYS2020_R01Dto>) => {
		postSortHandler(params, SYS2020_R01List, originalSYS2020_R01List, setSYS2020_R01List);
	}, [SYS2020_R01List, originalSYS2020_R01List, setSYS2020_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		description: `${SYS2020_R01List.length.toLocaleString()}건`,
	}]), [SYS2020_R01List.length]);

	return (
		<GridContextWrapper<SYS2020_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			setPaddedGridData={setSYS2020_R01List}
			initSelected={initSelectedSYS2020_R01List}
			allSelected={allSelectedSYS2020_R01List}
			defaultRowData={defaultSYS2020_R01Row()}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<SYS2020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<SYS2020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS2020_R01List,
					deletedList: deletedSYS2020_R01List,
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
